<template>
  <!-- Kontextbezogener Einstieg ins Hilfe-Center: kleines „?" neben einer
       Überschrift, verlinkt direkt auf den passenden Artikel
       (/hilfe?artikel=<id>). In Dialogen mit `new-tab`, damit offene
       Eingaben erhalten bleiben. -->
  <span class="help-link-host">
    <v-tooltip location="bottom" :text="label">
      <template #activator="{ props: tip }">
        <a
          v-bind="tip"
          :aria-label="label"
          class="help-link"
          :class="{ 'help-link--on-dark': onDark }"
          :data-testid="`help-link-${articleId}`"
          :href="href"
          :rel="newTab ? 'noopener' : undefined"
          :target="newTab ? '_blank' : undefined"
          @click="onClick"
        >
          <CircleHelp aria-hidden="true" :size="size" />
        </a>
      </template>
    </v-tooltip>
  </span>
</template>

<script setup lang="ts">
  import { CircleHelp } from 'lucide-vue-next'
  import { computed } from 'vue'
  import { useRouter } from 'vue-router'
  import { gt } from '@/i18n/gt'

  const props = withDefaults(defineProps<{
    /** ID des Artikels in src/pages/pageHelp/helpArticles.ts */
    articleId: string
    /** Im neuen Tab öffnen (für Dialoge, deren Eingaben erhalten bleiben sollen). */
    newTab?: boolean
    /** Helle Darstellung auf dunklem/farbigem Grund. */
    onDark?: boolean
    size?: number
  }>(), {
    newTab: false,
    onDark: false,
    size: 18,
  })

  const router = useRouter()
  const target = computed(() => ({ name: 'PageHelp', query: { artikel: props.articleId } }))
  const href = computed(() => router.resolve(target.value).href)
  const label = computed(() => gt('Hilfe zu diesem Thema'))

  function onClick (event: MouseEvent) {
    // Neuer Tab / Modifier-Klick: Browser-Standard. Sonst SPA-Navigation.
    if (props.newTab || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return
    event.preventDefault()
    router.push(target.value).catch(() => { /* gleiche Route */ })
  }
</script>

<style scoped>
.help-link-host {
  display: inline-flex;
  flex: 0 0 auto;
}

.help-link {
  display: inline-grid;
  place-items: center;
  width: 30px;
  height: 30px;
  flex: 0 0 auto;
  border-radius: 999px;
  color: var(--grey-color, #5f5f5f);
  background: transparent;
  text-decoration: none;
  transition: color 150ms ease, background-color 150ms ease, transform 150ms ease;
}

.help-link:hover {
  color: var(--primary-color, #0076e5);
  background: var(--primary-light-color, #deeefb);
}

.help-link:active {
  transform: scale(0.94);
}

.help-link:focus-visible {
  outline: 2px solid var(--primary-color, #0076e5);
  outline-offset: 2px;
}

.help-link--on-dark {
  color: rgba(255, 255, 255, 0.85);
}

.help-link--on-dark:hover {
  color: #fff;
  background: rgba(255, 255, 255, 0.16);
}

@media (prefers-reduced-motion: reduce) {
  .help-link {
    transition: none;
  }
}
</style>
